import { useState } from 'react'
import type { RerouteParams } from '@truepill/tpos-types'
import { ReactComponent as LocationIcon } from 'assets/icons/location.svg'
import { ReactComponent as RerouteIcon } from 'assets/icons/route.svg'
import { SaveButton } from 'components/ActionButton'
import IconWrapper from 'components/IconWrapper'
import { ButtonsContainer, InputContainer, ModalHeader, ModalWrapper } from 'components/Modal'
import { CancelButton } from 'components/PageStructure'
import Select from 'components/Select'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import styled from 'styled-components'
import { bodyPrimaryColor } from 'styles/styleVariables'

type BulkRerouteModalProps = {
  onConfirm: (params: RerouteParams) => void
  initialParams?: RerouteParams
  sourceLocationId?: string
}

const BulkRerouteModal = ({ onConfirm, initialParams, sourceLocationId }: BulkRerouteModalProps): JSX.Element => {
  const { dismissModal } = useModalContext()
  const { locations, getLocationNameById } = useTPCacheContext()
  const [locationId, setLocationId] = useState<string>(initialParams?.locationId ?? '')

  const selectedName = locationId ? getLocationNameById(locationId) : ''
  const selectValue = locationId ? { label: selectedName, value: locationId } : undefined

  const locationOptions = [...locations]
    .filter(loc => !!loc.active)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(loc => ({ label: loc.name, value: loc._id }))

  const canConfirm = !!locationId && locationId !== sourceLocationId

  const handleConfirm = () => {
    if (!canConfirm) return
    onConfirm({ locationId })
    dismissModal()
  }

  return (
    <ModalWrapper id="BulkRerouteModal">
      <ModalHeader>
        <IconWrapper>
          <RerouteIcon fill={bodyPrimaryColor} />
        </IconWrapper>
        <h2>Reroute orders</h2>
      </ModalHeader>
      <StyledInputContainer>
        <Select
          data-testid="BulkRerouteLocationSelector"
          modal
          options={locationOptions}
          value={selectValue}
          placeholder="Location"
          icon={<LocationIcon />}
          onChange={([option]: { value: string }[]) => {
            if (option?.value) setLocationId(option.value)
          }}
          showActive={!!locationId}
          disableClear
          showLoadingAnimation={false}
        />
      </StyledInputContainer>
      <ButtonsContainer>
        <CancelButton label="Cancel" onClick={dismissModal} />
        <SaveButton disabled={!canConfirm} label="Confirm" isModal onClick={handleConfirm} />
      </ButtonsContainer>
    </ModalWrapper>
  )
}

const StyledInputContainer = styled(InputContainer)`
  > button:first-child {
    height: 2.5rem;
  }
`

export default BulkRerouteModal
